import type {
  HeaderNavItemKind,
  HeaderNavItemVisibility,
} from "@circle-react/components/SettingsApp/Navigation/hooks/useFetchHeaderNavItems";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Link, type HistoryState } from "@tanstack/react-router";
import type { MouseEvent } from "react";

import { shouldRenderHeaderLinkAsAnchor } from "../../HeaderV3/constants";

const NAV_LINK_BASE_CLASSES =
  "flex items-center h-8 px-3 py-2 text-sm font-medium whitespace-nowrap rounded-md";

export interface NavigationLinkProps {
  dataTestId?: string;
  isActive?: boolean;
  isDisabled?: boolean;
  kind: HeaderNavItemKind;
  label: string;
  linkTo: string;
  onClick?: () => void;
  openInNewWindow?: boolean;
  renderAsAnchor?: boolean;
  visibility: HeaderNavItemVisibility;
}

export const NAV_LINK_INTERACTIVE_CLASSES = classNames(
  NAV_LINK_BASE_CLASSES,
  "transition-colors duration-150 focus-visible:outline-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 hover:bg-c-header-hover text-c-header hover:text-c-header-active focus-visible:text-c-header-active focus-visible:bg-c-header-hover",
);

export const NAV_LINK_ACTIVE_CLASSES =
  "!bg-c-header-active hover:!bg-c-header-active !text-c-header-active hover:!text-c-header-active focus-visible:!text-c-header-active";

const handleDisabledClick = (event: MouseEvent<HTMLAnchorElement>) => {
  event.preventDefault();
};

export const NavigationLink = ({
  linkTo,
  label,
  isActive = false,
  isDisabled = false,
  dataTestId,
  openInNewWindow = false,
  renderAsAnchor = false,
  kind,
  visibility,
  onClick,
}: NavigationLinkProps) => {
  const shouldRenderAsAnchorTag = shouldRenderHeaderLinkAsAnchor(
    kind,
    linkTo,
    renderAsAnchor,
  );

  const linkProps = {
    onClick: isDisabled ? handleDisabledClick : onClick,
    tabIndex: isDisabled ? -1 : 0,
    title: label,
    "data-testid": dataTestId,
    ...(openInNewWindow && { target: "_blank", rel: "noopener noreferrer" }),
  };

  const linkState =
    visibility === "admin_only"
      ? ({ visibility: "admin_only" } as HistoryState)
      : undefined;

  // Site pages, including the newsletter homepage, must be real anchors.
  if (!shouldRenderAsAnchorTag) {
    return (
      <Link
        to={linkTo}
        state={linkState}
        {...linkProps}
        activeOptions={{ includeSearch: false }}
        activeProps={{
          className: classNames(
            NAV_LINK_INTERACTIVE_CLASSES,
            NAV_LINK_ACTIVE_CLASSES,
          ),
        }}
        inactiveProps={{
          className: classNames(NAV_LINK_INTERACTIVE_CLASSES, {
            [NAV_LINK_ACTIVE_CLASSES]: isActive,
          }),
        }}
        aria-current={isActive ? "page" : undefined}
      >
        {label}
      </Link>
    );
  }

  // Render regular anchor for custom links and home link
  return (
    <a
      href={linkTo}
      {...linkProps}
      className={classNames(NAV_LINK_INTERACTIVE_CLASSES, {
        [NAV_LINK_ACTIVE_CLASSES]: isActive,
      })}
      aria-current={isActive ? "page" : undefined}
    >
      {label}
    </a>
  );
};
